import { useCallback, useEffect, useRef, useState } from 'react';

// Hoe lang er na de laatste toetsaanslag gewacht wordt. Kort genoeg om als
// "meteen" te voelen, lang genoeg dat het intypen van een restaurantnaam één
// verzoek oplevert en niet twintig.
const WACHTTIJD_MS = 1000;

// Oplopende wachttijden tussen herkansingen. Daarna blijft de status op 'fout'
// staan tot de gebruiker zelf opnieuw probeert: eindeloos door blijven proberen
// verbergt juist dat er iets mis is, en dat is precies wat hier niet mag.
const HERKANSINGEN_MS = [2000, 5000, 15000];

/**
 * Slaat losse velden vanzelf op, met een pauze na het typen.
 *
 * Geeft terug wat een statusregel nodig heeft (status, tijdstip, foutmelding)
 * plus `plan()` om een wijziging in te dienen. Wat er tijdens een lopend
 * verzoek bij komt, gaat mee in de volgende ronde; een mislukte ronde komt
 * terug in de wachtrij, maar onder de nieuwere waarden -- anders zou een
 * herkansing een correctie die de gebruiker net typte weer overschrijven.
 *
 * @param opslaan   async (velden) => void — krijgt alleen de gewijzigde velden
 * @param opties.wachttijd       pauze na de laatste wijziging, in ms
 * @param opties.onOpgegeven     aangeroepen als alle herkansingen op zijn
 * @param opties.ookOpenstaand   () => bool — extra reden om te waarschuwen bij
 *                               het verlaten van de pagina (secties met een
 *                               eigen opslaan-knop bijvoorbeeld)
 */
export function useAutosave(opslaan, opties = {}) {
  const { wachttijd = WACHTTIJD_MS, onOpgegeven, ookOpenstaand } = opties;

  // rust | wacht | bezig | opgeslagen | fout
  const [status, setStatus] = useState('rust');
  const [opgeslagenOp, setOpgeslagenOp] = useState(null);
  const [foutmelding, setFoutmelding] = useState(null);

  const wachtrij = useRef({});
  const timer = useRef(null);
  const bezig = useRef(false);
  const herkansing = useRef(0);

  // In refs, zodat de callbacks hieronder stabiel blijven en een hertekening
  // van het formulier de lopende timer niet weggooit.
  const opslaanRef = useRef(opslaan);
  opslaanRef.current = opslaan;
  const onOpgegevenRef = useRef(onOpgegeven);
  onOpgegevenRef.current = onOpgegeven;
  const ookOpenstaandRef = useRef(ookOpenstaand);
  ookOpenstaandRef.current = ookOpenstaand;
  const verstuurRef = useRef(null);

  const ietsOpenstaand = useCallback(
    () => Object.keys(wachtrij.current).length > 0 || bezig.current, []);

  const verstuur = useCallback(async () => {
    if (bezig.current) return;  // de lopende ronde kijkt straks zelf verder
    const lading = wachtrij.current;
    if (!Object.keys(lading).length) return;
    wachtrij.current = {};
    bezig.current = true;
    setStatus('bezig');
    try {
      await opslaanRef.current(lading);
      bezig.current = false;
      herkansing.current = 0;
      setFoutmelding(null);
      setOpgeslagenOp(new Date());
      if (Object.keys(wachtrij.current).length) {
        // Tijdens het verzoek is er alweer getypt.
        setStatus('wacht');
        clearTimeout(timer.current);
        timer.current = setTimeout(() => verstuurRef.current(), wachttijd);
      } else {
        setStatus('opgeslagen');
      }
    } catch (e) {
      bezig.current = false;
      wachtrij.current = { ...lading, ...wachtrij.current };
      setFoutmelding(e?.response?.data?.detail || e?.message || null);
      setStatus('fout');
      const wacht = HERKANSINGEN_MS[herkansing.current];
      if (wacht !== undefined) {
        herkansing.current += 1;
        clearTimeout(timer.current);
        timer.current = setTimeout(() => verstuurRef.current(), wacht);
      } else if (onOpgegevenRef.current) {
        onOpgegevenRef.current(e);
      }
    }
  }, [wachttijd]);
  verstuurRef.current = verstuur;

  const plan = useCallback((velden) => {
    wachtrij.current = { ...wachtrij.current, ...velden };
    herkansing.current = 0;  // nieuwe invoer: schone lei voor de herkansingen
    setStatus('wacht');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => verstuurRef.current(), wachttijd);
  }, [wachttijd]);

  const probeerOpnieuw = useCallback(() => {
    herkansing.current = 0;
    clearTimeout(timer.current);
    verstuurRef.current();
  }, []);

  // Waarschuwen bij het sluiten of herladen van het tabblad. Navigeren binnen
  // het dashboard gaat langs React Router en komt hier niet langs -- daarvoor
  // is de afhandeling hieronder.
  useEffect(() => {
    const waarschuw = (e) => {
      if (!ietsOpenstaand() && !(ookOpenstaandRef.current?.())) return;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', waarschuw);
    return () => window.removeEventListener('beforeunload', waarschuw);
  }, [ietsOpenstaand]);

  // Bij het verlaten van het scherm wordt een openstaande wijziging meteen
  // weggeschreven in plaats van weggegooid. Het verzoek loopt door nadat dit
  // scherm verdwenen is, dus de wijziging komt alsnog aan.
  useEffect(() => () => {
    clearTimeout(timer.current);
    const lading = wachtrij.current;
    wachtrij.current = {};
    if (Object.keys(lading).length && !bezig.current) {
      Promise.resolve(opslaanRef.current(lading)).catch(() => {});
    }
  }, []);

  return { status, opgeslagenOp, foutmelding, plan, probeerOpnieuw, ietsOpenstaand };
}
